import { useAutoAnimate } from '@formkit/auto-animate/react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CameraIcon,
  PaperClipIcon,
  PlusIcon
} from '@heroicons/react/16/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import LabelButton from 'components/inputs/label-button';
import NumberInput from 'components/inputs/number';
import Text from 'components/inputs/text';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger
} from 'components/layout/popover';
import { existingSystemComponentTypeProperties as componentTypeProperties } from 'data/constants';
import { ExistingSystemComponentType } from 'data/types';
import {
  setExistingSystems,
  updateExistingSystem,
  updateExistingSystemComponent
} from 'features/structure';
import { produce } from 'immer';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import { hasUsableLabelEvidence } from 'utils/label-evidence';
import { getProjectSettingValue } from 'utils/project';
import {
  determineModelNumberFromImage,
  determineSystemComponentFromModelNumber
} from 'utils/systems';
import { warningModal } from 'utils/ui';
import ExistingSystemComponentComponent from './existing-system-component';
import SendPhotoCollectorModal from './modals/send-photo-collector-modal';

export default function ExistingEquipment() {
  const dispatch = useAppDispatch();
  const { existingSystems } = useAppSelector((state) => state.structure);
  const { configurations, configurationOptions } = useAppSelector(
    (state) => state.configurations
  );
  const { selectedSystems } = useAppSelector((state) => state.systems);
  const [activeExistingSystem, setActiveExistingSystem] = useState(0);
  const [modelNumberInput, setModelNumberInput] = useState<string>('');
  const [photoCollectorOpen, setPhotoCollectorOpen] = useState(false);
  const [imageInput, setImageInput] = useState<File | null>(null);
  const [animate] = useAutoAnimate({ duration: 100 });

  const handleChangeNumberOfExistingSystems = (numberOfSystems: number) => {
    dispatch(
      setExistingSystems(
        produce(existingSystems, (draft) => {
          while (draft.length < numberOfSystems) {
            draft.push({
              uniqueId: nanoid(5),
              name: `System ${draft.length + 1}`,
              type:
                getProjectSettingValue('system_preference') === 'Self Contained'
                  ? 'ptac'
                  : 'ducted',
              components: []
            });
          }
          while (draft.length > numberOfSystems) {
            draft.pop();
          }
        })
      )
    );
  };

  const addExistingSystemModelNumber = async (modelNumber: string) => {
    const newComponentId = nanoid(5);
    const newComponent: ExistingSystemComponentType = {
      type: 'unknown',
      uniqueId: newComponentId,
      modelNumber,
      analyzing: true,
      replacing: true
    };
    dispatch(
      updateExistingSystem({
        ...existingSystems[activeExistingSystem],
        components: [
          ...existingSystems[activeExistingSystem].components,
          newComponent
        ]
      })
    );
    const newComponentProps = await determineSystemComponentFromModelNumber(
      modelNumber
    );
    dispatch(
      updateExistingSystemComponent({
        ...newComponent,
        ...newComponentProps,
        analyzing: false
      })
    );
  };

  const addExistingSystemImages = async (image: File) => {
    const newComponentId = nanoid(5);
    const newComponent: ExistingSystemComponentType = {
      type: 'unknown',
      uniqueId: newComponentId,
      analyzing: true,
      replacing: true
    };
    dispatch(
      updateExistingSystem({
        ...existingSystems[activeExistingSystem],
        components: [
          ...existingSystems[activeExistingSystem].components,
          newComponent
        ]
      })
    );
    const { modelNumber, evidence } = await determineModelNumberFromImage(
      image
    );
    if (modelNumber) {
      // Label evidence rides along to ground the lookup when the photo
      // yielded any; no evidence-only flow on this legacy page — behavior
      // with no model number stays as-is.
      const newComponentProps = hasUsableLabelEvidence(evidence)
        ? await determineSystemComponentFromModelNumber(
            modelNumber,
            undefined,
            evidence
          )
        : await determineSystemComponentFromModelNumber(modelNumber);
      dispatch(
        updateExistingSystemComponent({
          ...newComponent,
          ...newComponentProps,
          // manufactureDate is copied deterministically from the label
          // evidence (AHA-6796): the specs prompt never returns it, and the
          // component is freshly created here — no user value to respect.
          ...(evidence?.manufactureDate
            ? { manufactureDate: evidence.manufactureDate }
            : {}),
          analyzing: false
        })
      );
    }
  };

  const handleSelectPreviousSystem = () => {
    if (activeExistingSystem > 0) {
      setActiveExistingSystem(activeExistingSystem - 1);
      setModelNumberInput('');
      setImageInput(null);
    }
  };

  const handleSelectNextSystem = () => {
    if (activeExistingSystem < existingSystems.length - 1) {
      setActiveExistingSystem(activeExistingSystem + 1);
      setModelNumberInput('');
      setImageInput(null);
    }
  };

  const handleStartAnalysis = () => {
    if (modelNumberInput) {
      addExistingSystemModelNumber(modelNumberInput);
    } else if (imageInput) {
      addExistingSystemImages(imageInput);
    }
    setModelNumberInput('');
    setImageInput(null);
  };

  const addCustomComponent = (
    componentType: ExistingSystemComponentType['type']
  ) => {
    dispatch(
      updateExistingSystem({
        ...existingSystems[activeExistingSystem],
        components: [
          ...existingSystems[activeExistingSystem].components,
          {
            uniqueId: nanoid(5),
            type: componentType,
            replacing: true,
            analyzing: false
          }
        ]
      })
    );
  };

  useEffect(() => {
    if (
      configurations.length > 0 ||
      configurationOptions.length > 0 ||
      selectedSystems.length > 0
    ) {
      warningModal(
        'Editing existing equipment will reset all configurations and selected systems!'
      );
    }
  }, []);

  return (
    <div className='flex w-full flex-col items-center gap-5'>
      <span className='text-center text-2xl font-semibold'>
        How many systems are we replacing?
      </span>
      <div className='flex items-center gap-5'>
        {existingSystems.length > 1 && (
          <Button
            text='Previous'
            color='secondary'
            icon={ArrowLeftIcon}
            onClick={handleSelectPreviousSystem}
            disabled={activeExistingSystem === 0}
          />
        )}
        <div className='w-[70px]'>
          <NumberInput
            value={existingSystems.length}
            placeholder='1'
            onChange={(value) =>
              handleChangeNumberOfExistingSystems(value ?? 0)
            }
            debounce={500}
            invalid={!existingSystems.length}
            min={1}
          />
        </div>
        {existingSystems.length > 1 && (
          <Button
            text='Next'
            color='secondary'
            icon={ArrowRightIcon}
            iconPosition='right'
            onClick={handleSelectNextSystem}
            disabled={activeExistingSystem === existingSystems.length - 1}
          />
        )}
      </div>
      {existingSystems.length > 0 && (
        <fieldset
          key={existingSystems[activeExistingSystem]?.uniqueId}
          className='flex w-full flex-col items-center gap-5 rounded-lg border-2 border-gray-200 bg-white px-5 pb-5 pt-3'
          ref={animate}
        >
          <legend className='px-1 text-lg font-semibold'>
            {existingSystems[activeExistingSystem]?.name}{' '}
            {existingSystems[activeExistingSystem]?.components.length > 0 &&
              `(${
                existingSystems[activeExistingSystem]?.type === 'ducted'
                  ? 'Ducted'
                  : 'Self Contained'
              })`}
          </legend>
          <div className='flex w-full flex-col gap-3 rounded-lg bg-yellow-50 p-5'>
            <div className='text-center font-semibold'>Analyze Components:</div>
            <div className='flex w-full items-center gap-2'>
              <div className='shrink-0 grow'>
                <Text
                  value={modelNumberInput}
                  onChange={(value) => setModelNumberInput(value ?? '')}
                  placeholder='Model Number'
                />
              </div>
              <span className='px-1'>OR</span>
              <div className='w-[200px]'>
                <Button
                  icon={CameraIcon}
                  color='white'
                  text='Take photo with AI assistance'
                  onClick={() => setPhotoCollectorOpen(true)}
                  fullWidth
                />
              </div>
              <span className='px-1'>OR</span>
              <input
                id='images-input'
                type='file'
                accept='image/*'
                onChange={(e) =>
                  setImageInput(e.currentTarget.files?.[0] ?? null)
                }
                className='hidden'
              />
              <div className='w-[200px]'>
                <LabelButton
                  htmlFor='images-input'
                  icon={PaperClipIcon}
                  color='white'
                  text={imageInput ? 'Image Selected' : 'Upload Image'}
                  fullWidth
                />
              </div>
            </div>
            <div className='flex justify-center'>
              <Button
                text='Analyze'
                onClick={handleStartAnalysis}
                disabled={!modelNumberInput && !imageInput}
              />
            </div>
          </div>
          {existingSystems[activeExistingSystem]?.components.map(
            (component) => (
              <ExistingSystemComponentComponent
                key={component.uniqueId}
                component={component}
                activeExistingSystem={activeExistingSystem}
              />
            )
          )}
          <div className='flex w-full justify-start'>
            <Popover placement='bottom-start'>
              <div>
                <PopoverTrigger>
                  <Button
                    color='secondary'
                    text='Add Component'
                    icon={PlusIcon}
                  />
                </PopoverTrigger>
              </div>
              <PopoverContent className='z-50 flex w-[300px] flex-col rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5 focus:outline-none'>
                {Object.keys(componentTypeProperties).map((componentType) => (
                  <PopoverClose
                    key={componentType}
                    className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100'
                    onClick={() =>
                      addCustomComponent(
                        componentType as ExistingSystemComponentType['type']
                      )
                    }
                  >
                    <span>{componentType}</span>
                  </PopoverClose>
                ))}
              </PopoverContent>
            </Popover>
          </div>
        </fieldset>
      )}
      <SendPhotoCollectorModal
        openState={[photoCollectorOpen, setPhotoCollectorOpen]}
      />
    </div>
  );
}
